import React from 'react';

import { type IntlFormatters } from 'react-intl';
import Loadable from 'react-loadable';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { getExtensionManifest } from '@atlassian/company-hub-common/controllers/get-extension-manifest';
import type { CustomSitesExtensionProviderProps } from '@atlassian/company-hub-common/controllers/get-extension-manifest/types';
import type { HubDefaultParameters } from '@atlassian/company-hub-common/controllers/get-hub-default-parameters';

import { LINKS_MENU_EXTENSION_TYPE } from '../../constants';
import { type LinksMenuExtensionProps } from '../../index';
import {
	ALIGNMENT,
	BORDER,
	CORNERS,
	ITEM,
	type LinksMenuParameters,
	type Styles,
} from '../../types';
import { messages } from './messages';

const LINKS_MENU_VERSION_1 = '1';
const LinksMenuExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_LinksMenuExtension" */ '../links-menu-extension'
			)
		).LinksMenuExtension,
	loading: () => <UFOLoadHold name="links-menu-extension-loader"></UFOLoadHold>,
});

export const DEFAULT_STYLES: Styles = {
	alignment: ALIGNMENT.CENTER,
	fillColor: token('elevation.surface.overlay'),
	borderColor: token('color.background.accent.gray.subtler'),
	border: BORDER.NONE,
	cornerStyle: CORNERS.SHARP,
	textColor: token('color.text'),
	highlightColor: token('color.text'),
};

const getParameters = ({
	formatMessage,
	defaultParameters,
}: {
	defaultParameters?: HubDefaultParameters;
	formatMessage: IntlFormatters['formatMessage'];
}): LinksMenuParameters => ({
	extensionTitle: formatMessage(messages.lozengeTitle),
	version: LINKS_MENU_VERSION_1,
	styles: {
		...DEFAULT_STYLES,
		// Pre-v2 behaviour (preserved verbatim from master): when help-center
		// passes `defaultParameters` (legacy `var(--hub-primary-color, ...)`
		// shape), both `fillColor` AND `borderColor` mirror the brand-tinted
		// bg so the menu chip blends visually with the rest of the page chrome.
		...(defaultParameters?.backgroundColor
			? {
					fillColor: defaultParameters.backgroundColor,
					borderColor: defaultParameters.backgroundColor,
				}
			: {}),
		// Advanced-theming (v2) override: spec says the day-0 border for new
		// links-menu inserts should be the neutral `color.border` hex, not the
		// bg-mirroring shade above. Fires *after* the legacy block so it wins.
		// Gated explicitly on the v2 flag — legacy help-center, Confluence,
		// and CSM all see `isAdvancedThemingEnabled !== true` and skip this.
		...(defaultParameters?.isAdvancedThemingEnabled ? { borderColor: '#DCDFE4' } : {}),
		// Corner default is gate-aware. The legacy default is `CORNERS.SHARP`,
		// which the gated render maps to a 0px *local override* — so a brand-new
		// menu would be pinned to 0px and never follow the hub-wide radius. When
		// advanced theming is on, default to `CORNERS.ROUND` instead: it resolves
		// to "no override", so the menu inherits (and keeps tracking)
		// `--hub-corner-radius` until the admin picks an explicit value on the
		// slider. Gate off (Confluence Company Hub / CSM / legacy) keeps SHARP.
		...(defaultParameters?.isAdvancedThemingEnabled ? { cornerStyle: CORNERS.ROUND } : {}),
		...(defaultParameters?.textColor
			? {
					textColor: defaultParameters.textColor,
					highlightColor: defaultParameters.textColor,
				}
			: {}),
	},
	items: [
		{
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			itemId: uuid(),
			type: ITEM.LINK,
			title: formatMessage(messages.firstDefaultItem),
			link: '',
		},
		{
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			itemId: uuid(),
			type: ITEM.LINK,
			title: formatMessage(messages.secondDefaultItem),
			link: '',
		},
		{
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			itemId: uuid(),
			type: ITEM.MENU,
			title: formatMessage(messages.thirdDefaultItem),
			childItems: [
				{
					// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
					itemId: uuid(),
					type: ITEM.LINK,
					title: formatMessage(messages.nestedDefaultItem),
					link: '',
				},
			],
		},
	],
});

export const linksMenuExtensionProvider = async ({
	contentId,
	propsProvider,
	mode,
	defaultParameters,
}: LinksMenuExtensionProps): Promise<
	({
		editorActions,
		openConfigPanel,
		intl,
	}: CustomSitesExtensionProviderProps) => Promise<DefaultExtensionProvider<Parameters>>
> => {
	return async ({ editorActions, openConfigPanel, intl }: CustomSitesExtensionProviderProps) => {
		const parameters = getParameters({ formatMessage: intl.formatMessage, defaultParameters });
		return new DefaultExtensionProvider([
			getExtensionManifest<LinksMenuParameters>({
				title: intl.formatMessage(messages.lozengeTitle),
				type: LINKS_MENU_EXTENSION_TYPE,
				key: 'links-menu',
				description: intl.formatMessage(messages.linksMenuDescription),
				icons: {
					'48': () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_LinksMenuExtensionIcon" */
							'./icons/links-menu-extension-icon'
						).then((mod) => mod.LinksMenuExtensionIcon),
				},
				parameters,
				update: async () => {
					const selectedNode = editorActions?.getSelectedNode()?.toJSON();
					const selectedLocalId = selectedNode?.attrs?.localId;

					if (!!selectedLocalId && openConfigPanel) {
						openConfigPanel(selectedLocalId);
					}
				},
				render:
					async () =>
					({ node }: any) => {
						return (
							<LinksMenuExtension
								extensionNode={node}
								contentId={contentId}
								propsProvider={propsProvider}
								mode={mode}
							/>
						);
					},
				// @TODO add links menu upsell in COMPHUB-2408
				setUpsellModalSource: undefined,
				source: 'premiumCarouselInsert',
			}),
		]);
	};
};
